import React, { useState, useEffect } from "react";
import {
  validateContactData,
  type ContactErrors,
} from "../lib/booking-validation.ts";

interface Slot {
  time: string;
}

interface SessionType {
  id: "free" | "paid" | "paid_session";
  title: string;
  duration: string;
  price: string;
  description: string;
  icon: string;
}

const sessionTypes: SessionType[] = [
  {
    id: "free",
    title: "Entrevista gratuita",
    duration: "15 min",
    price: "0€",
    description: "Entrevista gratuita de acercamiento para conocernos.",
    icon: "💡",
  },
  {
    id: "paid",
    title: "Entrevista inicial",
    duration: "45 min",
    price: "35€",
    description: "Evaluación inicial completa para comenzar el proceso.",
    icon: "📋",
  },
  {
    id: "paid_session",
    title: "Sesión de intervención",
    duration: "50 min",
    price: "35€",
    description: "Sesión de intervención de Terapia Ocupacional.",
    icon: "💻",
  },
];

const CONTACT_FIELDS = ["name", "email", "phone"] as const;
type ContactField = (typeof CONTACT_FIELDS)[number];

const FIELD_META: Record<
  ContactField,
  { label: string; type: string; autoComplete: string }
> = {
  name: { label: "Nombre completo", type: "text", autoComplete: "name" },
  email: { label: "Email", type: "email", autoComplete: "email" },
  phone: { label: "Teléfono", type: "tel", autoComplete: "tel" },
};

type BookingUser = NonNullable<NonNullable<Window["manosactivasAjax"]>["user"]>;

/** What the logged-in profile already knows about a field — '' when it doesn't. */
const profileValue = (
  user: BookingUser | null,
  field: ContactField,
): string => {
  if (!user) return "";
  if (field === "name") return `${user.name} ${user.lastname || ""}`.trim();
  return (user[field] || "").trim();
};

export const BookingWizard: React.FC = () => {
  const [step, setStep] = useState(1);
  const [sessionType, setSessionType] = useState<SessionType | null>(null);
  const [problem, setProblem] = useState("");
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [slotsRetry, setSlotsRetry] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<ContactErrors>({});
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [isForOther, setIsForOther] = useState(false);
  const userData = window.manosactivasAjax?.user || null;

  // Fields the profile does not fill in. Rendering the summary INSTEAD of the
  // inputs used to trap anyone whose profile had no phone: the button stayed
  // disabled and the only way out was to pretend the appointment was for
  // somebody else. Now the summary stands and the gaps get their own inputs.
  //
  // Derived from the PROFILE, not from formData: keyed off what the user has
  // typed, the input would vanish under them at the first keystroke.
  const showSummary = Boolean(userData) && !isForOther;
  const missingFields = CONTACT_FIELDS.filter(
    (field) => !showSummary || !profileValue(userData, field),
  );

  useEffect(() => {
    if (userData && !isForOther) {
      setFormData({
        name: profileValue(userData, "name"),
        email: profileValue(userData, "email"),
        phone: profileValue(userData, "phone"),
      });
    } else if (isForOther) {
      setFormData({ name: "", email: "", phone: "" });
    }
  }, [userData, isForOther]);

  // Fetch slots from our WordPress API
  useEffect(() => {
    // Parse URL parameters
    const params = new URLSearchParams(window.location.search);
    const serviceParam = params.get("servicio");
    const bonoParam = params.get("bono");

    if (serviceParam || bonoParam) {
      let initialType: SessionType | undefined;

      if (serviceParam === "valoracion-gratuita" || serviceParam === "free") {
        initialType = sessionTypes.find((t) => t.id === "free");
      } else if (
        serviceParam === "asesoria" ||
        serviceParam === "terapia-online" ||
        serviceParam === "paid"
      ) {
        initialType = sessionTypes.find((t) => t.id === "paid");
      } else if (bonoParam) {
        initialType = sessionTypes.find((t) => t.id === "paid_session");
      }

      if (initialType) {
        setSessionType(initialType);
        setStep(2); // Jump to step 2 (Problem)
      }
    }
  }, []);

  useEffect(() => {
    if (step === 3 && sessionType) {
      setLoading(true);
      setSlotsError(null);
      fetch(
        `/wp-json/manosactivas/v1/booking/slots?event_type_id=${sessionType.id}`,
      )
        .then(async (res) => {
          const data = await res.json();
          // A REST error (e.g. Cal.com down / unconfigured) returns an object,
          // not an array — without this check it used to render as "no
          // availability" or crash on slots.reduce.
          if (!res.ok || !Array.isArray(data)) {
            throw new Error("slots_unavailable");
          }
          setSlots(data);
        })
        .catch(() => {
          setSlots([]);
          setSlotsError(
            "No se pudo cargar la disponibilidad. Inténtalo de nuevo en unos minutos o escríbenos a",
          );
        })
        .finally(() => setLoading(false));
    }
  }, [step, sessionType, slotsRetry]);

  const handleBooking = async () => {
    setLoading(true);
    setError(null);
    try {
      const restNonce = window.manosactivasAjax?.rest_nonce || "";
      const response = await fetch(
        "/wp-json/manosactivas/v1/booking/checkout",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": restNonce,
          },
          body: JSON.stringify({
            ...formData,
            problem,
            session_type: sessionType?.id,
            start_time: selectedSlot,
          }),
        },
      );

      const result = await response.json();

      if (result.url) {
        // Redirect to Stripe Checkout
        window.location.href = result.url;
        return;
      }

      if (result.success) {
        setStep(5); // Success for free sessions
      } else {
        setError(
          result.message ||
            "No se pudo completar la reserva. Inténtalo de nuevo.",
        );
      }
    } catch {
      setError("Error de conexión. Comprueba tu red e inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
      {/* Progress Bar */}
      <div className="h-2 bg-gray-100">
        <div
          className="h-full bg-primary transition-all duration-500 ease-out"
          style={{ width: `${(step / 5) * 100}%` }}
        />
      </div>

      <div className="p-8 md:p-12">
        {step === 1 && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h2 className="text-3xl font-bold text-dark mb-2">
              ¿Cómo podemos ayudarte?
            </h2>
            <p className="text-gray-500 mb-8">
              Selecciona el tipo de sesión que mejor se adapte a ti.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {sessionTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => {
                    setSessionType(type);
                    setStep(2);
                  }}
                  className="group p-6 text-left border-2 border-gray-100 rounded-2xl hover:border-primary hover:bg-primary/5 transition-all duration-300"
                >
                  <span className="text-4xl mb-4 block">{type.icon}</span>
                  <h3 className="text-xl font-bold text-dark group-hover:text-primary transition-colors">
                    {type.title}
                  </h3>
                  <p className="text-sm text-gray-500 mt-2 mb-4">
                    {type.description}
                  </p>
                  <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                    <span className="font-semibold text-primary">
                      {type.duration}
                    </span>
                    <span className="text-2xl font-bold text-dark">
                      {type.price}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-500">
            <button
              onClick={() => setStep(1)}
              className="text-primary font-medium mb-6 flex items-center gap-2"
            >
              ← Volver
            </button>
            <h2 className="text-3xl font-bold text-dark mb-2">
              Cuéntanos un poco más
            </h2>
            <p className="text-gray-500 mb-8">
              ¿Cuál es el motivo principal de tu consulta?
            </p>

            <label htmlFor="wizard-problem" className="sr-only">
              Motivo principal de tu consulta
            </label>
            <textarea
              id="wizard-problem"
              value={problem}
              onChange={(e) => setProblem(e.target.value)}
              placeholder="Ej: Dificultades con la escritura, asesoramiento sobre autonomía..."
              className="w-full p-4 h-40 border-2 border-gray-100 rounded-2xl focus:border-primary focus:outline-none transition-all resize-none"
            />

            <button
              onClick={() => setStep(3)}
              disabled={!problem.trim()}
              className="w-full mt-8 bg-primary text-white py-4 rounded-2xl font-bold text-lg hover:shadow-lg hover:shadow-primary/30 transition-all disabled:opacity-50"
            >
              Continuar a disponibilidad
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-500">
            <button
              onClick={() => setStep(2)}
              className="text-primary font-medium mb-6 flex items-center gap-2"
            >
              ← Volver
            </button>
            <h2 className="text-3xl font-bold text-dark mb-2">
              Elige fecha y hora
            </h2>
            <p className="text-gray-500 mb-8">
              Horarios disponibles según tu zona horaria.
            </p>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : slotsError ? (
              <div role="alert" className="text-center py-8">
                <p className="p-4 rounded-2xl border-2 border-accent/30 bg-accent/10 text-accent-dark mb-4">
                  {slotsError}{" "}
                  <a
                    href="mailto:info@manosactivas.es"
                    className="font-semibold underline"
                  >
                    info@manosactivas.es
                  </a>
                  .
                </p>
                <button
                  onClick={() => setSlotsRetry((n) => n + 1)}
                  className="btn btn-primary px-6"
                >
                  Reintentar
                </button>
              </div>
            ) : slots.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                No hay horarios disponibles.
              </p>
            ) : (
              <div className="space-y-8 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                {Object.entries(
                  slots.reduce((acc: Record<string, Slot[]>, slot) => {
                    const dateStr = new Date(slot.time).toLocaleDateString(
                      "es-ES",
                      { weekday: "long", day: "numeric", month: "long" },
                    );
                    if (!acc[dateStr]) acc[dateStr] = [];
                    acc[dateStr].push(slot);
                    return acc;
                  }, {}),
                ).map(([dateStr, daySlots]) => (
                  <div key={dateStr} className="animate-in fade-in">
                    <h3 className="font-bold text-dark capitalize mb-3 sticky top-0 bg-white py-2 z-10 border-b border-gray-50">
                      {dateStr}
                    </h3>
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                      {daySlots.map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => setSelectedSlot(slot.time)}
                          className={`p-3 text-sm font-medium rounded-xl border-2 transition-all ${
                            selectedSlot === slot.time
                              ? "border-primary bg-primary text-white shadow-md"
                              : "border-gray-100 hover:border-primary/30 hover:bg-primary/5"
                          }`}
                        >
                          {new Date(slot.time).toLocaleTimeString("es-ES", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setStep(4)}
              disabled={!selectedSlot}
              className="w-full mt-8 bg-primary text-white py-4 rounded-2xl font-bold text-lg hover:shadow-lg hover:shadow-primary/30 transition-all disabled:opacity-50"
            >
              Continuar a tus datos
            </button>
          </div>
        )}

        {step === 4 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-500">
            <button
              onClick={() => setStep(3)}
              className="text-primary font-medium mb-6 flex items-center gap-2"
            >
              ← Volver
            </button>
            <h2 className="text-3xl font-bold text-dark mb-2">
              Tus datos de contacto
            </h2>
            <p className="text-gray-500 mb-8">
              Necesitamos estos datos para enviarte el enlace de la sesión.
            </p>

            {userData && (
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isForOther}
                    onChange={(e) => setIsForOther(e.target.checked)}
                    className="rounded text-primary focus:ring-primary w-4 h-4"
                  />
                  <span className="text-sm font-medium">
                    ¿Esta cita es para otra persona?
                  </span>
                </label>
              </div>
            )}

            {showSummary && (
              <div className="p-4 bg-gray-50 rounded-xl mb-6">
                <p className="text-sm text-gray-500 mb-1">Reservando como:</p>
                <p className="font-semibold text-dark">
                  {userData!.name} {userData!.lastname}
                </p>
                <p className="text-sm text-gray-500">{userData!.email}</p>
                {userData!.phone && (
                  <p className="text-sm text-gray-500">{userData!.phone}</p>
                )}
              </div>
            )}

            {missingFields.length > 0 && (
              <div className="space-y-4">
                {showSummary && (
                  <p className="text-sm text-gray-600">
                    {missingFields.length === 1
                      ? "Falta un dato en tu perfil:"
                      : "Faltan algunos datos en tu perfil:"}
                  </p>
                )}
                {missingFields.map((field) => {
                  const { label, type, autoComplete } = FIELD_META[field];
                  const errorId = `wizard-${field}-error`;
                  const message = fieldErrors[field];
                  return (
                    <div key={field}>
                      <label
                        htmlFor={`wizard-${field}`}
                        className="block text-sm font-medium text-dark mb-1"
                      >
                        {label} *
                      </label>
                      <input
                        id={`wizard-${field}`}
                        type={type}
                        autoComplete={autoComplete}
                        required
                        value={formData[field]}
                        onChange={(e) =>
                          setFormData({ ...formData, [field]: e.target.value })
                        }
                        aria-invalid={message ? true : undefined}
                        aria-describedby={message ? errorId : undefined}
                        className={`w-full p-4 border-2 rounded-2xl focus:outline-none ${
                          message
                            ? "border-accent-deep"
                            : "border-gray-200 focus:border-primary"
                        }`}
                      />
                      {message && (
                        <p
                          id={errorId}
                          role="alert"
                          className="mt-1 text-sm text-accent-deep"
                        >
                          {message}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {error && (
              <div
                id="wizard-error"
                role="alert"
                className="mt-6 p-4 rounded-2xl border-2 border-accent/30 bg-accent/10 text-accent-deep text-sm font-medium"
              >
                {error}
              </div>
            )}

            <button
              onClick={() => {
                // The button is never disabled by validation: a dead button
                // with no explanation is what trapped people here before.
                const errors = validateContactData(formData);
                setFieldErrors(errors);
                if (Object.keys(errors).length > 0) return;
                setError(null);
                handleBooking();
              }}
              disabled={loading}
              className="w-full mt-8 bg-secondary-dark text-white py-4 rounded-2xl font-bold text-lg hover:shadow-lg hover:shadow-secondary/30 transition-all disabled:opacity-50 flex justify-center items-center gap-2"
            >
              {loading
                ? "Procesando..."
                : sessionType?.id === "free"
                  ? "Confirmar reserva gratis"
                  : `Pagar ${sessionType?.price} y Reservar`}
            </button>
          </div>
        )}

        {step === 5 && (
          <div className="text-center py-12 animate-in zoom-in duration-500">
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-3xl font-bold text-dark mb-4">
              ¡Reserva completada!
            </h2>
            <p className="text-gray-500 mb-8">
              Te hemos enviado un email con los detalles y el enlace de la
              videollamada.
            </p>
            <a href="/" className="btn btn-primary px-8">
              Volver al inicio
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
